<script setup lang="ts">
import { nextTick, onMounted, watch } from 'vue';
import { startUserActivity, stopUserActivity } from '@/common/services/userActivity';
import { retryPendingDeposits } from '@/common/services/pendingDeposits';
import { onLaunch, onShow, onHide } from '@dcloudio/uni-app';
import { useWalletStore } from '@/store/wallet';
import { useConfigStore } from '@/store/config';
import { useAppChromeStore } from '@/store/appChrome';
import { configureLanguage } from '@/locales/language-loader';
import { checkAuthOnAppLaunch } from '@/common/auth';
import {
  startOrderAutoConfirm,
  stopOrderAutoConfirm,
} from '@/common/services/orderAutoConfirm';
import { stopOrderSubmissionTicket } from '@/common/services/orderSubmissionTicket';
import { useLoginUiStore } from '@/store/loginUi';
const appChrome = useAppChromeStore();

/** 公告仅主 Tab（首页/交易/理财/资产）拉；合约/AI 等二级页不刷 active/unread */
const refreshAnnouncementsOnMainTab = (opts?: { force?: boolean }) => {
  if (!useWalletStore().isAuthenticated) return;
  if (!appChrome.isMainTabbarPath()) return;
  const now = Date.now();
  const last = Number((window as any).__pantaAnnFetchAt || 0);
  if (!opts?.force && now - last <= 15_000) return;
  try {
    (window as any).__pantaAnnFetchAt = now;
  } catch {
    /* ignore */
  }
  void import('@/store/announcements').then(({ useAnnouncementsStore }) => {
    const ann = useAnnouncementsStore();
    void ann.fetchActive();
    void ann.fetchUnreadCount();
  });
};

/** H5：等首屏 Vue 挂上再关 index.html splash，避免 DOMContentLoaded 竞速白屏 */
onMounted(() => {
  void import('@/common/utils/preloadChromeImages').then(({ preloadChromeImages }) => {
    preloadChromeImages();
  });
  void nextTick(() => {
    requestAnimationFrame(() => {
      try {
        const ready = (window as any).__PANTA_APP_READY__;
        if (typeof ready === 'function') ready();
      } catch {
        /* ignore */
      }
    });
  });
});

// 标志：防止重复检查登录弹窗
let hasCheckedLoginModal = false;
let depositRetryTimer: ReturnType<typeof setInterval> | null = null;
let foreground = false;
watch(() => [useWalletStore().isAuthenticated,useWalletStore().address], () => {
  if(foreground)void retryPendingDeposits();
  // 登录成功：仅主 Tab 刷公告；二级页不拉
  if (useWalletStore().isAuthenticated) {
    refreshAnnouncementsOnMainTab({ force: true });
  } else {
    void import('@/store/announcements').then(({ useAnnouncementsStore }) => {
      const ann = useAnnouncementsStore();
      ann.unreadCount = 0;
      ann.bannerVisible = false;
    });
  }
});

onLaunch(async (options) => {
  // 语言：先同步落本地/系统 locale，IP 探测在后台（不挡冷启动）
  void configureLanguage();
  appChrome.syncFromRoute();

  // 清理可能存在的旧 authCode 缓存（避免影响钱包登录）
  try {
    localStorage.removeItem('wallet_bind_auth_code');
  } catch (error) {
    console.warn('[App] Failed to remove old authCode:', error);
  }

  // 清掉模拟模式遗留的 mock-wallet-* token，避免联调时带着假 Bearer
  try {
    const { clearTokenStorage, getToken } = await import('@/common/services/request');
    const existing = getToken();
    if (existing && String(existing).startsWith('mock-')) {
      clearTokenStorage();
      console.info('[App] Cleared legacy mock token');
    }
  } catch (error) {
    console.warn('[App] Failed to clear mock token:', error);
  }

  // 从 URL 获取 authCode 并更新本地存储（邀请码场景）；普通访问无 authCode 属正常
  try {
    const authCode = getAuthCodeFromUrl();
    if (authCode) {
      localStorage.setItem('authCode', authCode);
    } else {
      localStorage.removeItem('authCode');
    }
  } catch (error) {
    console.warn('[App] Failed to handle authCode:', error);
  }

  // Initialize wallet store
  const walletStore = useWalletStore();
  
  // 页面刷新时立即重置连接状态，避免按钮一直显示"连接中"
  walletStore.$patch({
    isConnecting: false,
    error: null,
  });
  
  // Initialize config store (local cache + 拉取 platform-coin 精度，供 J18 头/换算)
  const configStore = useConfigStore();
  configStore.init();
  void configStore.loadDictionary().catch(() => false);

  // Get referrer code from URL query parameters
  let referrerCode = options?.query?.ref as string | undefined;

  // 检查 URL 中的 ref 参数
  const hash = window.location.hash;
  if (!referrerCode && hash && hash.includes('?')) {
    const hashParams = new URLSearchParams(hash.split('?')[1] || '');
    referrerCode = hashParams.get('ref') || undefined;
  }
  
  // 也检查 search params
  if (!referrerCode) {
    const urlParams = new URLSearchParams(window.location.search);
    referrerCode = urlParams.get('ref') || undefined;
  }

  // Set the referrer code in the store if it exists
  if (referrerCode) {
    walletStore.setReferrerCode(referrerCode);
  }

  // Initialize wallet state (restores connection, etc.)
  // refresh 已在 restoreAuthenticatedSession 内尝试；Cookie 失效时的 401 不影响本地 JWT 会话
  walletStore.init().then(() => {
    // 仅已登录才检查绑定弹窗；稍延后等 layout 挂载（刷新恢复会话）
    if (walletStore.isAuthenticated) {
      walletStore.emitBindInviteCheck(900);
      // 二次兜底：H5 首屏 layout 偶发晚于首次 emit
      walletStore.emitBindInviteCheck(1800);
    }
    checkAuthOnAppLaunch();
    startOrderAutoConfirm();
    // 冷启公告：等路由落到主 Tab 后再由 layout 拉；此处仅当已在主 Tab 才刷
    setTimeout(() => refreshAnnouncementsOnMainTab({ force: true }), 0);
    // 受理凭证预取改由合约/AI 交易页 enterOrderTicketSurface，避免全站空刷 submission

    setTimeout(() => {
      checkLoginModal();
    }, 500);
  });
});

onShow(() => {
  startUserActivity();
  foreground=true;
  void retryPendingDeposits();
  if(!depositRetryTimer)depositRetryTimer=setInterval(()=>{void retryPendingDeposits();},10_000);
  // 每次进前台 / 页面切换后同步 chrome 显隐
  appChrome.syncFromRoute();
  startOrderAutoConfirm();
  // 回前台：对齐扩展/DApp 当前账户，并补完跳转钱包后的登录意图
  const walletStore = useWalletStore();
  void walletStore.syncProviderAccount('app-show');
  void walletStore.resumePendingWalletLogin('app-show');
  // submission 预取仅由合约/AI 页 enterOrderTicketSurface 维护，勿在 App onShow 空刷
  void import('@/common/services/request').then(({ getToken, refreshIfDue }) => {
    if (getToken()) void refreshIfDue();
  });
  // 主 Tab 公告：延后一帧，等 redirect 后的路由落稳（避免进合约时误判）
  setTimeout(() => refreshAnnouncementsOnMainTab(), 0);
});

onHide(() => {
  stopUserActivity();
  foreground=false;
  if(depositRetryTimer)clearInterval(depositRetryTimer);
  depositRetryTimer=null;
  stopOrderAutoConfirm();
  stopOrderSubmissionTicket();
});

// 从 URL 获取 authCode
const getAuthCodeFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  let authCode = urlParams.get('authCode');
  
  if (!authCode) {
    const hash = window.location.hash;
    if (hash && hash.includes('?')) {
      const hashParams = new URLSearchParams(hash.split('?')[1] || '');
      authCode = hashParams.get('authCode');
    }
  }
  
  return authCode;
};

// 检查是否需要显示登录弹窗（邀请码场景：只弹窗，不自动验签登录）
const checkLoginModal = async () => {
  if (hasCheckedLoginModal) {
    return;
  }
  
  const authCode = getAuthCodeFromUrl();
  hasCheckedLoginModal = true;

  if (!authCode) {
    return;
  }

  // 有邀请码时仅引导用户手动连接，禁止自动 challenge / 签名
  try {
    localStorage.setItem('authCode', authCode);
  } catch {
    // ignore
  }
  showLoginModal(authCode);
};

// 显示登录弹窗
const showLoginModal = (authCode?: string) => {
  // 延迟显示，确保页面已经完全加载
  setTimeout(() => {
    const walletStore = useWalletStore();
    // 如果已经登录，不显示弹框
    if (walletStore.isAuthenticated && walletStore.address) {
      console.log('[App] User already authenticated, skipping login modal');
      return;
    }
    console.log('[App] Opening login modal', authCode ? 'with authCode' : '');
    try {
      useLoginUiStore().open(authCode);
    } catch {
      uni.$emit('openLoginModal', { authCode });
    }
  }, 1500);
};
</script>

<template>
  <view class="app-root">
    <router-view />
  </view>
</template>

<style lang="scss">
.app-root {
  width: 100%;
  min-height: 100%;
}

/* Apply a base reset to common elements, avoiding the universal selector */
page, view, div, span, a, p, h1, h2, h3, h4, h5, h6, ul, li, button {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* Specifically reset the box-sizing for the wallet modal to its default */
w3m-modal * {
  box-sizing: content-box !important;
}

page {
  height: 100%;
  font-family: MiSans VF;
  background-color: #f7f8fa;
}

/* 禁用下拉刷新和过度滚动 (H5) */
html, body {
  overscroll-behavior: none;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  background-color: #f7f8fa;
}

/* H5 / 平板：内容区最大宽度由 --app-max-width 控制，居中显示 */
#app {
  width: 100%;
  min-height: 100%;
  background-color: #f7f8fa;
}

uni-page-body,
uni-page-wrapper,
.uni-page-body,
.uni-page-wrapper {
  width: 100% !important;
  max-width: var(--app-max-width, 750px) !important;
  height: 100% !important;
  margin-left: auto !important;
  margin-right: auto !important;
  background-color: #f7f8fa;
  box-sizing: border-box;
}

/* 指定页（如市场详情）铺满视口，取消手机中栏 */
html.app-shell-fullscreen uni-page-body,
html.app-shell-fullscreen uni-page-wrapper,
html.app-shell-fullscreen .uni-page-body,
html.app-shell-fullscreen .uni-page-wrapper {
  max-width: none !important;
  width: 100% !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
  box-shadow: none !important;
}

@media screen and (min-width: 768px) {
  uni-page-body,
  uni-page-wrapper,
  .uni-page-body,
  .uni-page-wrapper {
    box-shadow: 0 0 0 1px rgba(17, 24, 39, 0.04), 0 12px 40px rgba(17, 24, 39, 0.06);
  }

  html.app-shell-fullscreen uni-page-body,
  html.app-shell-fullscreen uni-page-wrapper,
  html.app-shell-fullscreen .uni-page-body,
  html.app-shell-fullscreen .uni-page-wrapper {
    box-shadow: none !important;
  }
}

/* 禁用页面级别的下拉刷新 */
page {
  overscroll-behavior-y: none;
}

/* uni toast 默认 8em 宽且失败也会带成功勾；纯文案时在屏宽内换行 */
.uni-toast {
  width: auto !important;
  max-width: min(78vw, 420px) !important;
  min-width: 0;
  padding: 12px 16px;
  box-sizing: border-box;
}

.uni-toast__content,
.uni-simple-toast__text {
  white-space: normal !important;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.45;
}
</style>
